import type { EditMediaRequest, Media } from '@nucleus-commerce/api-client/admin';

export interface Size {
  width: number;
  height: number;
}

/** Fractions of the picture after rotating and flipping, as the backend takes them. */
export interface CropRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export type QuarterTurn = 0 | 90 | 180 | 270;

export const ASPECTS = ['free', 'original', 'square', '4x5', '3x4', '16x9'] as const;
export type AspectId = (typeof ASPECTS)[number];

const FIXED_ASPECTS: Partial<Record<AspectId, number>> = {
  square: 1,
  '4x5': 4 / 5,
  '3x4': 3 / 4,
  '16x9': 16 / 9,
};

export type CropHandle = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';
export const CORNER_HANDLES: readonly CropHandle[] = ['nw', 'ne', 'se', 'sw'];
export const EDGE_HANDLES: readonly CropHandle[] = ['n', 'e', 's', 'w'];

export interface ResizeTarget extends Size {
  background: string;
}

export interface ImageEditDraft {
  rotate: QuarterTurn;
  flipHorizontal: boolean;
  flipVertical: boolean;
  crop: CropRect;
  aspect: AspectId;
  /** Null keeps the cropped picture's own size. */
  resize: ResizeTarget | null;
}

export const WHOLE_IMAGE: CropRect = { x: 0, y: 0, width: 1, height: 1 };
export const DEFAULT_BACKGROUND = '#ffffff';
/** A crop can't shrink below this share of the picture, so its handles stay apart. */
const MIN_CROP = 0.02;

export const EMPTY_DRAFT: ImageEditDraft = {
  rotate: 0,
  flipHorizontal: false,
  flipVertical: false,
  crop: WHOLE_IMAGE,
  aspect: 'free',
  resize: null,
};

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

/** Only these can be rewritten: the backend leaves videos, documents and GIFs as they are. */
export function isEditableImage(media: Pick<Media, 'kind' | 'mimeType'>): boolean {
  return media.kind === 'image' && media.mimeType !== 'image/gif';
}

/**
 * The picture's real size, upright. `media.width` and `height` are the stored pixels, which an EXIF
 * rotation can swap, and the preview is a smaller rendition, so the preview gives the shape and the
 * record the scale.
 */
export function uprightSize(media: Pick<Media, 'width' | 'height'>, preview: Size | null): Size {
  const longest = Math.max(media.width ?? 0, media.height ?? 0);
  if (!preview) return { width: media.width ?? 1, height: media.height ?? 1 };
  if (longest === 0) return preview;
  const scale = longest / Math.max(preview.width, preview.height);
  return { width: Math.round(preview.width * scale), height: Math.round(preview.height * scale) };
}

export function frameSize(image: Size, rotate: QuarterTurn): Size {
  return rotate % 180 === 0 ? image : { width: image.height, height: image.width };
}

export function croppedSize(frame: Size, crop: CropRect): Size {
  return {
    width: Math.max(1, Math.round(frame.width * crop.width)),
    height: Math.max(1, Math.round(frame.height * crop.height)),
  };
}

/** Width over height in pixels; null is freeform. */
function aspectRatio(aspect: AspectId, frame: Size): number | null {
  if (aspect === 'free') return null;
  return aspect === 'original' ? frame.width / frame.height : (FIXED_ASPECTS[aspect] ?? null);
}

/** The same ratio over the crop's fractions, which is what the drag works in. */
export function cropRatio(aspect: AspectId, frame: Size): number | null {
  const ratio = aspectRatio(aspect, frame);
  return ratio === null ? null : ratio / (frame.width / frame.height);
}

/** The picture turns as `rotate` then the flips, so one more turn after a single flip runs backwards. */
export function rotateClockwise(draft: ImageEditDraft): ImageEditDraft {
  const { x, y, width, height } = draft.crop;
  const mirrored = draft.flipHorizontal !== draft.flipVertical;
  return {
    ...draft,
    rotate: ((draft.rotate + (mirrored ? 270 : 90)) % 360) as QuarterTurn,
    crop: { x: 1 - y - height, y: x, width: height, height: width },
    // A portrait preset turned on its side is no longer that preset.
    aspect: draft.aspect === 'square' || draft.aspect === 'original' ? draft.aspect : 'free',
    resize: null,
  };
}

export function flipHorizontal(draft: ImageEditDraft): ImageEditDraft {
  const { crop } = draft;
  return {
    ...draft,
    flipHorizontal: !draft.flipHorizontal,
    crop: { ...crop, x: 1 - crop.x - crop.width },
  };
}

export function flipVertical(draft: ImageEditDraft): ImageEditDraft {
  const { crop } = draft;
  return {
    ...draft,
    flipVertical: !draft.flipVertical,
    crop: { ...crop, y: 1 - crop.y - crop.height },
  };
}

/** Picks a shape: the largest centered crop that has it. Freeform keeps the crop as it is. */
export function selectAspect(draft: ImageEditDraft, aspect: AspectId, frame: Size): ImageEditDraft {
  const ratio = cropRatio(aspect, frame);
  if (ratio === null) return { ...draft, aspect };
  const width = ratio >= 1 ? 1 : ratio;
  const height = ratio >= 1 ? 1 / ratio : 1;
  return {
    ...draft,
    aspect,
    crop: { x: (1 - width) / 2, y: (1 - height) / 2, width, height },
    resize: null,
  };
}

export function moveCrop(crop: CropRect, dx: number, dy: number): CropRect {
  return {
    ...crop,
    x: clamp(crop.x + dx, 0, 1 - crop.width),
    y: clamp(crop.y + dy, 0, 1 - crop.height),
  };
}

/** Drags one handle; with a `ratio` the corner opposite stays put and the shape holds. */
export function resizeCrop(
  crop: CropRect,
  handle: CropHandle,
  dx: number,
  dy: number,
  ratio: number | null,
): CropRect {
  const right = crop.x + crop.width;
  const bottom = crop.y + crop.height;
  const movesWest = handle.includes('w');
  const movesEast = handle.includes('e');
  const movesNorth = handle.includes('n');
  const movesSouth = handle.includes('s');
  const left = movesWest ? clamp(crop.x + dx, 0, right - MIN_CROP) : crop.x;
  const top = movesNorth ? clamp(crop.y + dy, 0, bottom - MIN_CROP) : crop.y;
  const newRight = movesEast ? clamp(right + dx, crop.x + MIN_CROP, 1) : right;
  const newBottom = movesSouth ? clamp(bottom + dy, crop.y + MIN_CROP, 1) : bottom;
  if (ratio === null) {
    return { x: left, y: top, width: newRight - left, height: newBottom - top };
  }

  const maxWidth = movesWest ? right : 1 - crop.x;
  const maxHeight = movesNorth ? bottom : 1 - crop.y;
  let width = Math.max(newRight - left, (newBottom - top) * ratio, MIN_CROP);
  let height = width / ratio;
  if (width > maxWidth) {
    width = maxWidth;
    height = width / ratio;
  }
  if (height > maxHeight) {
    height = maxHeight;
    width = height * ratio;
  }
  return {
    x: movesWest ? right - width : crop.x,
    y: movesNorth ? bottom - height : crop.y,
    width,
    height,
  };
}

const isWhole = ({ x, y, width, height }: CropRect) =>
  x <= 0 && y <= 0 && width >= 1 && height >= 1;

export function hasEdits(draft: ImageEditDraft): boolean {
  return (
    draft.rotate !== 0 ||
    draft.flipHorizontal ||
    draft.flipVertical ||
    !isWhole(draft.crop) ||
    draft.resize !== null
  );
}

export function toEditBody(draft: ImageEditDraft): EditMediaRequest {
  return {
    rotate: draft.rotate,
    flipHorizontal: draft.flipHorizontal,
    flipVertical: draft.flipVertical,
    ...(isWhole(draft.crop) ? {} : { crop: draft.crop }),
    ...(draft.resize ? { resize: draft.resize } : {}),
  };
}
